import axios from "axios";
import { updateProject } from "./projectStore";

const BASE = process.env.REACT_APP_BACKEND_URL;

export async function seedCCBNicheProject(token: string): Promise<string> {
  const authHeader = { Authorization: `Bearer ${token}` };

  // ── 1. Create project ─────────────────────────────────────────────────────
  const { data: project } = await axios.post(`${BASE}/api/projects`, {
    name: "Niche Research — AI Tools Affiliate",
    description:
      "Running CCB's niche-opportunity-finder skill: identify and validate an untapped AI/SaaS affiliate niche with ≥3 programs at 20%+ commission.",
    status: "active",
    methodology: "agile",
    sprintLength: 2,
    diagramText: `flowchart LR
  A[Clarify Goals] --> B[Generate 8-12 Candidates]
  B --> C[Score on 4 Dimensions]
  C --> D[Validate Top 3 on CCB]
  D --> E[Write Opportunity Brief]
  E --> F[Map to Next Skill]
  F --> G[affiliate-program-search]
  F --> H[commission-calculator]`,

    stakeholders: [
      {
        id: "sh-1",
        name: "Affiliate Marketer",
        role: "Primary User",
        interest: "Identify a profitable niche to start promoting within 1 hour",
        influence: "high",
        email: "",
      },
      {
        id: "sh-2",
        name: "Content Team",
        role: "Consumer of Output",
        interest: "Receive actionable content angles for the top niche",
        influence: "medium",
        email: "",
      },
      {
        id: "sh-3",
        name: "Affiliate Program Manager",
        role: "External Partner",
        interest: "Quality traffic from well-researched niche content",
        influence: "low",
        email: "",
      },
    ],

    documents: [
      {
        id: "doc-1",
        type: "brd",
        title: "Niche Opportunity Brief — AI Tools",
        content: `# Niche Opportunity Brief

## Top Niche: AI Productivity Tools
**Tier:** A (Trending)
**Score:** 8.4 / 10

| Dimension | Score | Weight | Weighted |
|---|---|---|---|
| Search Demand | 9 | 30% | 2.7 |
| Program Availability | 8 | 30% | 2.4 |
| Competition Level | 7 | 25% | 1.75 |
| Content Potential | 9 | 15% | 1.35 |

## Why This Niche
- AI tools market growing 35% YoY
- Buyers are professionals with high LTV
- SaaS programs pay 20–40% recurring commission
- Low barrier: no need for personal expertise, just honest reviews

## Example Programs
1. **Jasper AI** — 30% recurring, 60-day cookie
2. **Copy.ai** — 45% first month + 20% recurring
3. **Writesonic** — 30% recurring lifetime

## Content Angles
1. "Best AI writing tools for non-native English speakers"
2. "ChatGPT vs Jasper: which saves more time for marketers?"
3. "5 AI tools that replaced my VA (with honest pricing)"

## Runner-Up: AI Image Generation
**Score:** 7.1 — fewer high-commission programs, more competition

## Next Steps
1. Run \`affiliate-program-search\` for Jasper, Copy.ai, Writesonic
2. Run \`commission-calculator\` for 90-day projections
3. Create first article using Blog & SEO skills`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ],

    userStories: [
      {
        id: "us-1",
        title: "Generate niche candidates",
        asA: "affiliate marketer",
        iWant: "8-12 niche candidates across Trending, Evergreen, and Micro-niche tiers",
        soThat: "I have enough options to compare objectively",
        acceptanceCriteria: [
          "At least 3 candidates from Tier A (trending)",
          "At least 3 from Tier B (evergreen)",
          "At least 2 Tier C micro-niches",
          "Each candidate has a brief rationale",
        ],
        priority: "must",
        storyPoints: 3,
        status: "done",
        startDate: new Date(Date.now() - 10 * 86400000).toISOString(),
        dueDate: new Date(Date.now() - 8 * 86400000).toISOString(),
        estimatedDays: 2,
      },
      {
        id: "us-2",
        title: "Score niches on 4 dimensions",
        asA: "affiliate marketer",
        iWant: "each niche scored on Search Demand, Program Availability, Competition, Content Potential",
        soThat: "I can compare them with a single objective score",
        acceptanceCriteria: [
          "Weights applied correctly (30/30/25/15)",
          "Each score backed by a data source or SERP check",
          "Final scores shown in sortable table",
        ],
        priority: "must",
        storyPoints: 5,
        status: "done",
        startDate: new Date(Date.now() - 8 * 86400000).toISOString(),
        dueDate: new Date(Date.now() - 5 * 86400000).toISOString(),
        estimatedDays: 3,
      },
      {
        id: "us-3",
        title: "Validate top 3 on CCB",
        asA: "affiliate marketer",
        iWant: "the top 3 niches verified against list.affitor.com",
        soThat: "I know programs are real, active, and paying",
        acceptanceCriteria: [
          "Each niche has ≥3 programs with 20%+ commission confirmed",
          "Cookie window ≥30 days on top niche",
          "No programs flagged as low-quality or inactive",
        ],
        priority: "must",
        storyPoints: 3,
        status: "in-progress",
        startDate: new Date(Date.now() - 4 * 86400000).toISOString(),
        dueDate: new Date(Date.now() + 1 * 86400000).toISOString(),
        estimatedDays: 5,
      },
      {
        id: "us-4",
        title: "Deliver opportunity brief",
        asA: "affiliate marketer",
        iWant: "a structured brief for the winning niche with content angles and next steps",
        soThat: "I can start promoting within 1 hour of receiving it",
        acceptanceCriteria: [
          "Top niche named with final score",
          "3 specific content angles listed",
          "Next skill mapped (affiliate-program-search)",
          "Runner-up summarised in 2 lines",
        ],
        priority: "must",
        storyPoints: 2,
        status: "backlog",
        startDate: new Date(Date.now() + 2 * 86400000).toISOString(),
        dueDate: new Date(Date.now() + 4 * 86400000).toISOString(),
        estimatedDays: 2,
      },
    ],
  }, { headers: authHeader });

  const projectId: string = project.id;

  // ── 2. Seed requirements via tools API ────────────────────────────────────
  const requirements = [
    {
      title: "Generate 8-12 niche candidates",
      raw_text:
        "The system must identify between 8 and 12 niche candidates covering all three tiers: Trending (A), Evergreen (B), and Micro-niche (C).",
      req_type: "functional",
      priority: "must",
      domain: "research",
    },
    {
      title: "Top niche has ≥3 programs at 20%+ commission",
      raw_text:
        "The winning niche must have at least 3 verified affiliate programs each offering minimum 20% commission rate. Verified via list.affitor.com.",
      req_type: "functional",
      priority: "must",
      domain: "validation",
    },
    {
      title: "Search demand backed by data",
      raw_text:
        "Every niche score for Search Demand dimension must cite a specific data source (Google Trends, Ahrefs, SimilarWeb, or SERP inspection).",
      req_type: "non-functional",
      priority: "must",
      domain: "quality",
    },
    {
      title: "Content angles must be specific",
      raw_text:
        "Content angles in the brief must name a specific audience segment and angle — not generic titles like 'AI tool review'.",
      req_type: "non-functional",
      priority: "should",
      domain: "quality",
    },
    {
      title: "Self-validation checklist must pass",
      raw_text:
        "Before delivery, the skill must run its internal validation: all four scoring dimensions complete, programs verified, brief formatted correctly.",
      req_type: "constraint",
      priority: "must",
      domain: "process",
    },
  ];

  for (const req of requirements) {
    try {
      await axios.post(
        `${BASE}/api/tools/requirements`,
        { ...req, project_id: projectId },
        { headers: authHeader }
      );
    } catch {
      // non-fatal — requirements panel will show what was saved
    }
  }

  return projectId;
}
